import { gql } from "@apollo/client";
import { IApplicant, IOffers, IProcess, IRequest } from "types/Jobyfine.type";
import { EUserSeniority } from "types/User.types";

export interface IGetApplicantsInput {
  page: number;
  pageSize: number;
  textInput: string;
  tags?: string[];
  profiles?: string[];
  additionalFilters?: string;
  countries?: string[];
  referredBy?: string;
}

export interface IGetApplicantsResponse {
  getApplicantsPagination: {
    data: IApplicant[];
    total: number;
    totalPages: number;
    totalWithAICV: number;
    totalWithCV: number;
  };
}

const GET_APPLICANTS_PAGINATED = gql`
  query GetApplicantsPagination($getApplicantsInput: GetApplicantsInput!) {
    getApplicantsPagination(getApplicantsInput: $getApplicantsInput) {
      data {
        id
        name
        lastname
        email
        cvName
        cvUrl
        incomeExpectation
        incomeExpectationCurrency
        locationId
        postulations
        country
        birthday
        phone
        rut
        region
        transformedToUserId
        tags
        profiles
        englishLvl
        blackListed
        internalCandidate
        referredBy
        roles {
          role
          vendorId
        }
        notes {
          id
          note
          date
          createdBy
        }
        processes {
          id
          offer {
            id
            name
          }
        }
        aiCVInfo {
          ofuscatedName
          education {
            dateEnd
            dateStart
            description
            title
          }
          extraEducation {
            dateEnd
            dateStart
            description
            title
          }
          languages {
            level
            name
          }
          tagLine
          techSkills
          workExperience {
            date
            description
            title
            company
          }
          extraWorkExperience {
            date
            description
            title
            company
          }
          certifications {
            name
            date
          }
        }
      }
      total
      totalPages
      totalWithAICV
      totalWithCV
    }
  }
`;

export interface IGetAllOfferInput {
  searchText: string;
  state?: string;
}

export interface IGetAllOfferRes {
  getOffers: IOffers[];
}

const PUBLIC_OFFERS = gql`
  query GetOffersByHostname {
    getOffersByHostname {
      id
      name
      vancancy
      contractType
      country
      publishedAt
      area {
        id
        name
      }
    }
  }
`;

const PUBLIC_VENDOR = gql`
  query VendorByHostname {
    vendorByHostname {
      id
      jobyfinePortalDescription
      jobyfinePortalSlogan
      jobyfinePortalTitle
      logo
      name
      address {
        address
      }
    }
  }
`;

const GET_OFFERS = gql`
  query getOffers($getAllOffersInput: GetAllOffersInput!) {
    getOffers(getAllOffersInput: $getAllOffersInput) {
      id
      name
      description
      vancancy
      fake
      contractType
      country
      client {
        id
        name
      }
      area {
        id
        name
      }
      createdAt
      image
      state
      stateHistory {
        oldState
        newState
        date
      }
      typeOffer
      publicEnd
      processOffer {
        id
        label
        state
      }
      requests {
        id
        name
      }
      maxSalary
      currency
      definition
      tags
      profiles
      recruiter
      requester {
        id
        name
      }
    }
  }
`;

const GET_OFFERS_LIGHT = gql`
  query getOffers($getAllOffersInput: GetAllOffersInput!) {
    getOffers(getAllOffersInput: $getAllOffersInput) {
      id
      name
      state
      quantityOfApplicants
      client {
        id
        name
      }
      requests {
        id
        name
        identifier
      }
    }
  }
`;

const GET_OFFERS_BY_IDS = gql`
  query getOffersByIds($ids: [String!]!) {
    getOffersByIds(ids: $ids) {
      id
      name
      state
      quantityOfApplicants
      client {
        id
        name
      }
      requests {
        id
        name
        identifier
      }
    }
  }
`;

const GET_JOBYFINE_REQUESTER = gql`
  query JobyfineRequesters {
    jobyfineRequesters {
      id
      name
      lastname
      profileImage
      email
      role {
        id
        name
      }
    }
  }
`;

const GET_JOBYFINE_RECRUITER = gql`
  query JobyfineRecruiters {
    jobyfineRecruiters {
      id
      name
      lastname
      profileImage
      email
      role {
        id
        name
      }
    }
  }
`;

export interface IGetAllRequest {
  getAllRequest: IRequest[];
}

const GET_ALL_REQUESTS = gql`
  query GetAllRequest {
    getAllRequest {
      id
      name
      createdAt
      contractType
      type
      crmId
      opportunityMargin
      deadline
      clientScheduleStart
      clientScheduleEnd
      projectDuration
      specialShifts
      locationRestriction
      seniority
      client {
        id
        name
      }
      serviceType
      vacancies
      country
      modality
      techStack
      requiresCertificationOrDegree
      keyFunctionsAndResponsibilities
      isEquipmentProvidedByClientOrUs
      hasClientInterview
      processStagesAndClientEvaluators
      specialShiftsDetails
      locationRestrictionDetails
      files {
        name
        blob64
        size
      }
      description
      maxRent
      currency
      area {
        id
        name
      }
      creator {
        id
        name
        email
        lastname
        profileImage
      }
      assignedRecruiter {
        id
        name
        email
        lastname
        profileImage
      }
      offers {
        id
        name
        description
        vancancy
        fake
        client {
          id
          name
        }
        area {
          id
          name
        }
        createdAt
        image
        state
        stateHistory {
          oldState
          newState
          date
        }
        typeOffer
        publicEnd
        processOffer {
          id
          label
          state
        }
        requests {
          id
          name
        }
        maxSalary
        currency
        definition
        requester {
          id
          name
        }
        recruiter
      }
    }
  }
`;

const GET_OFFERS_BY_ID = gql`
  query getOfferById($id: String!) {
    getOfferById(id: $id) {
      id
      name
      description
      vancancy
      country
      client {
        id
        name
      }
      area {
        id
        name
      }
      createdAt
      image
      state
      typeOffer
      publicEnd
      processOffer {
        id
        label
        state
      }
      maxSalary
      currency
      tags
      knowledges {
        id
        name
      }
      recruiter
      requester {
        id
        name
      }
      vendor {
        id
        jobyfinePortalTitle
      }
    }
  }
`;

export interface IGetAllProcessByIdOfferInputRes {
  getAllProcessByIdOffer: IProcess[];
}
export interface IGetAllProcessByIdOfferInput {
  getAllProcessByIdOfferInput: {
    offerId: string;
  };
}

const GET_ALL_PROCESS = gql`
  query getAllProcessByIdOffer(
    $getAllProcessByIdOfferInput: GetAllProcessByIdOfferInput!
  ) {
    getAllProcessByIdOffer(
      getAllProcessByIdOfferInput: $getAllProcessByIdOfferInput
    ) {
      id
      offerId
      status
      offer {
        id
        name
      }
      user {
        id
        name
        lastname
        rut
        country
        email
        cvName
        cvUrl
        incomeExpectation
        incomeExpectationCurrency
        locationId
        region
        tags
        postulations
        phone
        englishLvl
        blackListed
        internalCandidate
        referredBy
        createdBy {
          id
          name
          lastName
          email
          imageUrl
        }
        processes {
          id
          userId
          offerId
          fitByCV {
            justifications
            score
          }
          offer {
            id
            name
          }
        }

        aiCVInfo {
          ofuscatedName
          tagLine
        }

        notes {
          id
          note
          date
          createdBy
        }
      }
    }
  }
`;

const GET_FULL_APPLICANT = gql`
  query GetApplicant {
    getApplicant {
      id
      name
      password
      email
      active
      networkType
      lastname
      profileImage
      address
      locationId
      remoteWork
      incomeExpectation
      rut
      phone
      birthday
      createdAt
      updatedAt
      cvUrl
      cvName
      postulations
      cvUploaded
      preferencesCheck
      country
      incomeExpectationCurrency
      tags
      profiles
      referredBy
    }
  }
`;

const GET_APPLICANT_BY_ID = gql`
  query GetApplicantById($getApplicantByIdId: String!) {
    getApplicantById(id: $getApplicantByIdId) {
      id
      name
      lastname
      email
      cvName
      cvUrl
      incomeExpectation
      incomeExpectationCurrency
      locationId
      postulations
      country
      birthday
      phone
      rut
      region
      tags
      profiles
      englishLvl
      transformedToUserId
      blackListed
      internalCandidate
      referredBy
      roles {
        role
        vendorId
      }
      notes {
        id
        note
        date
        createdBy
      }
      processes {
        id
        offerId
        status
        offer {
          id
          name
        }
      }
      aiCVInfo {
        ofuscatedName
        education {
          dateEnd
          dateStart
          description
          title
        }
        extraEducation {
          dateEnd
          dateStart
          description
          title
        }
        languages {
          level
          name
        }
        tagLine
        techSkills
        workExperience {
          date
          description
          title
          company
        }
        extraWorkExperience {
          date
          description
          title
          company
        }
        certifications {
          name
          date
        }
        applicantLinks {
          icon
          url
        }
      }
      emailHistory {
        id
        offerId
        offerStateId
        sended
        error
        date
      }
    }
  }
`;

const GET_APPLICANT_BY_EMAIL = gql`
  query GetApplicantByEmail($email: String!) {
    getApplicantByEmail(email: $email) {
      id
      name
      lastname
      email
    }
  }
`;

const GET_OFFERS_PAGINATED = gql`
  query GetOffersPagination(
    $getOfferPaginationInput: GetOfferPaginationInput!
  ) {
    getOffersPagination(getOfferPaginationInput: $getOfferPaginationInput) {
      total
      totalPages
      totalPublic
      totalDraft
      data {
        id
        name
        description
        vancancy
        fake
        contractType
        country
        client {
          id
          name
        }
        area {
          id
          name
        }
        createdAt
        image
        state
        stateHistory {
          oldState
          newState
          date
          reasonToChangeState
        }
        typeOffer
        publicEnd
        processOffer {
          id
          label
          state
        }
        requests {
          id
          name
        }
        maxSalary
        currency
        definition
        tags
        profiles
        recruiter
        requester {
          id
          name
        }
      }
    }
  }
`;

const FIT_APPLICANT_TO_OFFER = gql`
  query OfferFitWithApplicant($offerId: String!, $applicantId: String!) {
    offerFitWithApplicant(offerId: $offerId, applicantId: $applicantId) {
      justifications
      score
    }
  }
`;

const DOWNLOAD_REQUESTS_REPORT = gql`
  query Query {
    downloadRequest
  }
`;

export interface IGetRequestPaginationInput {
  page: number;
  pageSize: number;
  searchText?: string;
  areas?: string[];
  recruiter?: string | null;
}

export interface GetRequestPaginationResponse {
  getRequestPagination: IGetRequest;
}
export interface IGetRequest {
  data: IRequest[];
  total: number;
  totalPages: number;
  totalRequestWithRecruiter: number;
  totalOffersAssociatedWithRequest: number;
}

const GET_REQUEST_PAGINATED = gql`
  query GetRequestPagination(
    $getRequestPaginationInput: GetRequestPaginationInput!
  ) {
    getRequestPagination(
      getRequestPaginationInput: $getRequestPaginationInput
    ) {
      total
      totalPages
      totalOffersAssociatedWithRequest
      totalRequestWithRecruiter
      data {
        role
        ceaTalentCLP
        topeMaximoCEA
        brutoCLP
        baseCLP
        ceaComercialUF
        ceaSeniority
        id
        identifier
        requestDefinition
        requestDefinitionJustification
        name
        createdAt
        contractType
        type
        crmId
        opportunityMargin
        deadline
        client {
          id
          name
        }
        notes {
          id
          note
          date
          createdBy
        }
        changeHistory {
          updateBy {
            email
            lastName
            name
            profileImage
            role
          }
          updateField {
            newValue
            oldValue
            field
          }
          updateAt
        }
        clientScheduleStart
        clientScheduleEnd
        projectDuration
        specialShifts
        locationRestriction
        seniority
        serviceType
        vacancies
        country
        modality
        techStack
        requiresCertificationOrDegree
        keyFunctionsAndResponsibilities
        isEquipmentProvidedByClientOrUs
        hasClientInterview
        processStagesAndClientEvaluators
        specialShiftsDetails
        locationRestrictionDetails
        englishLevel
        files {
          name
          blob64
          size
        }
        description
        maxRent
        currency
        area {
          id
          name
        }
        creator {
          id
          name
          email
          lastname
          profileImage
        }
        assignedRecruiter {
          id
          name
          email
          lastname
          profileImage
        }

        offers {
          id
          name
          description
          vancancy
          fake
          client {
            id
            name
          }
          area {
            id
            name
          }
          createdAt
          image
          state
          stateHistory {
            oldState
            newState
            date
          }
          typeOffer
          publicEnd
          processOffer {
            id
            label
            state
          }
          requests {
            id
            name
          }
          maxSalary
          currency
          definition
          requester {
            id
            name
          }
          recruiter
        }
      }
    }
  }
`;

export interface IJobyfineDashboardRange {
  from: string;
  to: string;
  clientId?: string;
  recruiterIds?: string[];
  activeOnly?: boolean;
}

export interface IJobyfineKpiSummary {
  offers: number;
  recruiters: number;
  requestLinkCoveragePercent: number;
  avgOpenVacancyDays: number;
  lostVacancies: number;
}

export interface IRecruiterEffectivenessRow {
  recruiterId?: string;
  recruiterLabel: string;
  offersAssigned: number;
  offersClosed: number;
  offersCanceled: number;
  closingRate?: number;
  hires: number;
  sentToClient: number;
  conversionRate?: number;
}

export interface IOfferStateDistributionBucket {
  state: string;
  count: number;
}

export interface ISentToClientTrendPoint {
  month: string;
  sentToClient: number;
  hires: number;
  conversionRate?: number;
}

export interface IStandbyCancelledReason {
  offerId: string;
  offerName: string;
  recruiterLabel?: string;
  newState: string;
  date: string;
  reason: string;
}

export interface ILeadTimeMetrics {
  processDurationDays?: number;
  realLeadTimeDays?: number;
  offersWithRequestLink: number;
  totalOffers: number;
  coveragePercent: number;
}

const GET_JOBYFINE_KPI_SUMMARY = gql`
  query JobyfineKpiSummary($range: JobyfineDashboardRangeInput!) {
    jobyfineKpiSummary(range: $range) {
      offers
      recruiters
      requestLinkCoveragePercent
      avgOpenVacancyDays
      lostVacancies
    }
  }
`;

const GET_RECRUITER_EFFECTIVENESS = gql`
  query RecruiterEffectiveness($range: JobyfineDashboardRangeInput!) {
    recruiterEffectiveness(range: $range) {
      recruiterId
      recruiterLabel
      offersAssigned
      offersClosed
      offersCanceled
      closingRate
      hires
      sentToClient
      conversionRate
    }
  }
`;

const GET_OFFER_STATE_DISTRIBUTION = gql`
  query OfferStateDistribution($range: JobyfineDashboardRangeInput!) {
    offerStateDistribution(range: $range) {
      state
      count
    }
  }
`;

const GET_SENT_TO_CLIENT_TREND = gql`
  query SentToClientTrend($range: JobyfineDashboardRangeInput!) {
    sentToClientTrend(range: $range) {
      month
      sentToClient
      hires
      conversionRate
    }
  }
`;

const GET_STANDBY_CANCELLED_REASONS = gql`
  query StandbyCancelledReasons($range: JobyfineDashboardRangeInput!) {
    standbyCancelledReasons(range: $range) {
      offerId
      offerName
      recruiterLabel
      newState
      date
      reason
    }
  }
`;

const GET_LEAD_TIME_METRICS = gql`
  query LeadTimeMetrics($range: JobyfineDashboardRangeInput!) {
    leadTimeMetrics(range: $range) {
      processDurationDays
      realLeadTimeDays
      offersWithRequestLink
      totalOffers
      coveragePercent
    }
  }
`;

const GET_APPLICANT_EXTRA_ATTRIBUTES = gql`
  query GetUserExtraAttributesByApplicantId($applicantId: String!) {
    getUserExtraAttributesByApplicantId(applicantId: $applicantId) {
      id
      accountNumber
      address
      afp
      applicantId
      assignableToProjects
      bankAccountType
      bankName
      baseSalary
      birthCountry
      personalEmail
      children {
        birthDate
        identityDocument
        name
      }
      cleverId
      cleveritEmail
      cleveritLogin
      clientProvidesComputer
      collaboratorEmail
      createdAt
      division
      emergencyContacts {
        id
        number
        type
      }
      firstLastName
      firstName
      grossSalary
      headEmail
      healthSystem
      healthSystemName
      identityDocument
      isapre
      isSubstitution
      manager
      headEmail
      maritalStatus
      phoneNumber
      position
      workCountry
      workModality
      tshirtSize
      residenceCountry
      paymentPlatform
      dietaryConsiderations
      linkedin
      facebook
      instagram
    }
  }
`;

export enum GenderEnum {
  MALE = "MALE",
  FEMALE = "FEMALE",
  OTHER = "OTHER"
}

// TypeScript interface for the response
export interface IGetUserExtraAttributesByApplicantIdResponse {
  getUserExtraAttributesByApplicantId: {
    id: string;
    userId?: string;
    applicantId?: string;
    // Personal Information
    firstName: string;
    firstLastName: string;
    secondLastName: string;
    identityDocument: string;
    cleverId: string;
    maritalStatus: string;
    gender: GenderEnum;
    birthCountry: string;
    residenceCountry: string;
    residenceCity: string;
    address: string;
    phoneNumber: string;
    personalEmail?: string; // Added missing field
    birthDate?: string; // Added missing field
    // Work Information
    division: string;
    area?: string; // Added missing field
    manager: string;
    position: string;
    seniority?: string; // Added missing field
    collaboratorEmail: string;
    headEmail: string;
    managerEmail: string;
    // Banking Information
    bankAccountType: string;
    accountNumber: string;
    bankName?: string;
    // Chilean Specific Information
    afp: string;
    healthSystem: string;
    healthSystemName?: string;
    isapre?: string;
    isapreAmount?: number;
    // Payment Information
    paymentPlatform: string;
    workCountry: string;
    workModality: string;
    contractType: string;
    startDate: Date;
    computerDeliveryDate?: Date;
    registrationDate: Date;
    baseSalary: number;
    grossSalary: number;
    currency: string;
    // Operational Data
    assignableToProjects?: boolean;
    isSubstitution?: boolean;
    substitutionFor?: string;
    requiresComputer?: boolean;
    clientProvidesComputer?: boolean;
    requiresCellPhone?: boolean;
    computerBonusApplies?: boolean;
    // Equipment Information
    computerType?: string;
    computerBrand?: string;
    serialNumber?: string;
    operatingSystem?: string;
    mobileOS?: string;
    // Company Information
    cleveritEmail?: string;
    cleveritLogin?: string;
    temporaryPassword?: string;
    // Additional Information
    dietaryConsiderations?: string;
    linkedin?: string;
    facebook?: string;
    instagram?: string;
    tshirtSize: string;
    profession: string;
    emergencyContacts: Array<{
      id: string;
      type: string;
      number: string;
    }>;
    children: Array<{
      name: string;
      birthDate: string;
      identityDocument: string;
    }>;
    extraArchives?: string[];
    // New Cloud Requirements Fields
    requiresComputerShipping?: boolean;
    nationality?: string;
    upn?: string;
    password?: string;
    createdAt: Date;
    updatedAt: Date;
    requireComputer: boolean;
    seniorityLevel?: EUserSeniority;
  } | null;
}

// GraphQL query
export const GET_USER_EXTRA_ATTRIBUTES_BY_APPLICANT_ID = gql`
  query GetUserExtraAttributesByApplicantId($applicantId: String!) {
    getUserExtraAttributesByApplicantId(applicantId: $applicantId) {
      id
      userId
      applicantId
      firstName
      firstLastName
      birthDate
      secondLastName
      identityDocument
      cleverId
      maritalStatus
      gender
      birthCountry
      residenceCountry
      residenceCity
      address
      phoneNumber
      personalEmail
      area
      division
      manager
      position
      collaboratorEmail
      headEmail
      managerEmail
      bankAccountType
      accountNumber
      bankName
      afp
      healthSystem
      healthSystemName
      isapre
      isapreAmount
      paymentPlatform
      workCountry
      workModality
      contractType
      startDate
      computerDeliveryDate
      registrationDate
      baseSalary
      grossSalary
      currency
      assignableToProjects
      isSubstitution
      substitutionFor
      requiresComputer
      clientProvidesComputer
      requiresCellPhone
      computerBonusApplies
      computerType
      computerBrand
      serialNumber
      operatingSystem
      mobileOS
      cleveritEmail
      cleveritLogin
      temporaryPassword
      dietaryConsiderations
      linkedin
      facebook
      instagram
      tshirtSize
      profession
      seniorityLevel
      emergencyContacts {
        id
        type
        number
      }
      children {
        name
        birthDate
        identityDocument
      }
      extraArchives
      # New Cloud Requirements Fields
      requiresComputerShipping
      nationality
      upn
      password
      createdAt
      updatedAt
      requireComputer
    }
  }
`;

export default {
  GET_APPLICANT_EXTRA_ATTRIBUTES,
  GET_OFFERS,
  GET_APPLICANTS_PAGINATED,
  GET_JOBYFINE_RECRUITER,
  GET_JOBYFINE_REQUESTER,
  GET_ALL_REQUESTS,
  GET_REQUEST_PAGINATED,
  GET_OFFERS_BY_ID,
  GET_ALL_PROCESS,
  PUBLIC_VENDOR,
  PUBLIC_OFFERS,
  GET_FULL_APPLICANT,
  GET_APPLICANT_BY_ID,
  GET_APPLICANT_BY_EMAIL,
  GET_OFFERS_LIGHT,
  GET_OFFERS_BY_IDS,
  GET_OFFERS_PAGINATED,
  FIT_APPLICANT_TO_OFFER,
  DOWNLOAD_REQUESTS_REPORT,
  GET_USER_EXTRA_ATTRIBUTES_BY_APPLICANT_ID,
  GET_JOBYFINE_KPI_SUMMARY,
  GET_RECRUITER_EFFECTIVENESS,
  GET_OFFER_STATE_DISTRIBUTION,
  GET_SENT_TO_CLIENT_TREND,
  GET_STANDBY_CANCELLED_REASONS,
  GET_LEAD_TIME_METRICS
};
